import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Globe, Menu, X, Check } from "lucide-react";
import { useLang, useLocalePath } from "../contexts/LanguageContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navItems = [
  { path: "/", key: "home" },
  { path: "/about", key: "about" },
  { path: "/services", key: "services" },
  { path: "/training", key: "training" },
  { path: "/products", key: "products" },
  { path: "/gallery", key: "gallery" },
  { path: "/blog", key: "blog" },
  { path: "/contact", key: "contact" },
];

export default function Navbar() {
  const { t, lang, setLang, langs } = useLang();
  const localePath = useLocalePath();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLangChange = (code) => {
    try { localStorage.setItem("lsp_lang", code); } catch {}
    setLang(code);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ivory/90 border-b border-dust/60 shadow-[0_6px_24px_-18px_rgba(24,61,41,0.35)]"
          : "backdrop-blur-sm bg-ivory/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-[72px] lg:h-[84px]">
          <Link to={localePath("/")} data-testid="nav-logo" className="flex items-center gap-3 group">
            <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-full overflow-hidden ring-1 ring-forest/20 bg-ivory shrink-0">
              <img src="/images/logo.png" alt="Loka Samabaya Pratisthan logo" className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight hidden sm:block">
              <div className="font-serif text-[17px] lg:text-[19px] text-forest">{t("meta.site_name")}</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-clay">{t("meta.tagline")}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Main">
            {navItems.map((it) => (
              <NavLink
                key={it.key}
                to={localePath(it.path)}
                end={it.path === "/"}
                data-testid={`nav-${it.key}-link`}
                className={({ isActive }) =>
                  `link-sweep text-[14px] font-medium tracking-wide transition-colors ${
                    isActive ? "text-forest" : "text-ink/70 hover:text-forest"
                  }`
                }
              >
                {t(`nav.${it.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="language-switcher-btn"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-dust hover:border-forest/40 hover:bg-kraft/60 transition-colors text-[13px]"
                  aria-label="Change language"
                >
                  <Globe className="w-4 h-4 text-forest" />
                  <span className="font-medium uppercase tracking-wider text-forest">{lang}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-ivory border-dust">
                {langs.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => handleLangChange(l.code)}
                    className={`cursor-pointer flex items-center justify-between gap-6 ${lang === l.code ? "bg-kraft text-forest" : ""}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-[11px] uppercase tracking-widest text-clay">{l.code}</span>
                      <span>{l.native}</span>
                    </span>
                    {lang === l.code && <Check className="w-3.5 h-3.5 text-forest" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to={localePath("/contact")} data-testid="nav-contact-cta" className="hidden md:inline-flex btn-pill btn-forest text-[13px] py-2.5 px-5">
              {t("nav.get_in_touch")}
            </Link>

            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-forest"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div data-testid="mobile-menu" className="lg:hidden py-4 border-t border-dust/70">
            <div className="flex flex-col gap-1">
              {navItems.map((it) => (
                <NavLink
                  key={it.key}
                  to={localePath(it.path)}
                  end={it.path === "/"}
                  data-testid={`mobile-nav-${it.key}-link`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-[15px] ${
                      isActive ? "bg-kraft text-forest" : "text-ink/80 hover:bg-kraft/60"
                    }`
                  }
                >
                  {t(`nav.${it.key}`)}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
